import { useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { ArrowUpDownIcon, CalendarIcon, EllipsisVerticalIcon } from 'lucide-react'
import {
	Badge,
	Button,
	DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
	EmptyState,
	IconButton,
	IllustrationEmpty,
	PanelFilters,
	SearchInput,
	Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
	Spinner,
	Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
	Tabs, TabsList, TabsTrigger,
} from '@rentflowdev/ui'
import type { IncompleteReason, PaymentStatus, Rental, RentalPeriodicity, RentalStatus } from '@/types'
import { formatPropertyAddress } from '@/lib/property-address'
import {
	CURRENCY_SIGNS,
	DEFAULT_LOCALE,
	INCOMPLETE_REASONS, INCOMPLETE_REASON_KEYS, INCOMPLETE_REASON_VARIANTS,
	PAYMENT_STATUSES, PAYMENT_STATUS_KEYS, PAYMENT_STATUS_VARIANTS,
	RENTAL_PERIODS,
} from '@/types'
import { useListContactsQuery, useListPropertiesQuery, useListRentalsQuery } from '@/api'
import { PanelDown } from '@/components/panel-down'
import { StartRentalDialog } from '@/components/start-rental-dialog'
import { EditRentalDialog } from '@/components/edit-rental-dialog'
import { durationLabel } from '@/lib/duration-label'
import { useCan } from '@/lib/access'

const PAGE_SIZE = 10

type SortField = 'property' | 'tenant' | 'price' | 'nextPayment' | 'startDate' | 'daysUntilStart'
type SortDir = 'asc' | 'desc'

function formatPrice(amount: string | number, currency: string, period: Rental['period'], t: (key: string) => string): string {
	const num = typeof amount === 'string' ? Number(amount) : amount
	const formatted = (Number.isFinite(num) ? num : 0).toLocaleString(DEFAULT_LOCALE)
	const sign = CURRENCY_SIGNS[currency] ?? currency
	const periodLabel = t(`rent.periods.${period.toLowerCase()}`)
	return `${formatted}${sign}/${periodLabel}`
}

function formatDate(iso: string) {
	return new Date(iso).toLocaleDateString(DEFAULT_LOCALE, { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function daysUntil(iso: string): number {
	const ms = new Date(iso).getTime() - Date.now()
	return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)))
}

function daysUntilVariant(days: number): 'warning' | 'success' {
	if (days <= 30) return 'warning'
	return 'success'
}

type ClosingScope = 'ALL' | 'ACTIVE' | 'CLOSING'

interface RentalListPageProps {
	status: RentalStatus
	emptyTitle: string
	emptyDescription: string
	initialPropertyId?: string
	/** «Текущие» section: ACTIVE + CLOSING with a Все|Активные|На закрытии
	 * tab filter instead of a separate sidebar item (nav Variant 2). */
	withClosingTabs?: boolean
}

export function RentalListPage({ status, emptyTitle, emptyDescription, initialPropertyId, withClosingTabs }: RentalListPageProps) {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const [search, setSearch] = useState('')
	const [page, setPage] = useState(1)
	const [sortField, setSortField] = useState<SortField>('nextPayment')
	const [sortDir, setSortDir] = useState<SortDir>('asc')
	const [propertyFilter, setPropertyFilter] = useState(initialPropertyId ?? '')
	const [tenantFilter, setTenantFilter] = useState('')
	const [periodFilter, setPeriodFilter] = useState<RentalPeriodicity | ''>('')
	const [paymentFilter, setPaymentFilter] = useState<PaymentStatus | ''>('')
	const [reasonFilter, setReasonFilter] = useState<IncompleteReason | ''>('')
	const [scope, setScope] = useState<ClosingScope>('ALL')
	const [addOpen,          setAddOpen]          = useState(false)
	const [selectedRental,   setSelectedRental]   = useState<Rental | null>(null)
	const [extendOpen,       setExtendOpen]       = useState(false)

	// The page behaves per the selected scope tab; «Все» renders like the
	// ACTIVE view with a per-row «На закрытии» badge for CLOSING rentals.
	const effectiveStatus: RentalStatus = withClosingTabs
		? (scope === 'CLOSING' ? 'CLOSING' : 'ACTIVE')
		: status
	const isClosing = effectiveStatus === 'CLOSING'
	const isUpcoming = effectiveStatus === 'UPCOMING'
	const isCompleted = effectiveStatus === 'COMPLETED'
	const canCreate = useCan('create', 'Rental')
	const showAddButton = canCreate && (effectiveStatus === 'ACTIVE' || effectiveStatus === 'UPCOMING')
	const showPaymentFilter = effectiveStatus === 'ACTIVE'
	const showReasonFilter = effectiveStatus === 'CLOSING'
	const showScopeBadges = withClosingTabs && scope === 'ALL'

	// Rental.propertyTitle/Address/Image/tenantName ожидаются от бека
	// (см. project_dto_gaps_pending.md → Rental + Deal denormalization).
	const { data, isLoading, error, refetch } = useListRentalsQuery(
		withClosingTabs
			? scope === 'ALL'
				? { statuses: ['ACTIVE', 'CLOSING'] }
				: { status: scope }
			: { status }
	)
	const rentals = useMemo<Rental[]>(() => data?.rentals ?? [], [data])

	// Запросы для опций фильтров — нужны все объекты/арендаторы юзера, не только из текущего списка аренд.
	const { data: propertiesData } = useListPropertiesQuery({ limit: 200 })
	const { data: contactsData }   = useListContactsQuery({ type: 'TENANT', limit: 200 })

	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase()
		return rentals
			.filter((r) => !propertyFilter || r.propertyId === propertyFilter)
			.filter((r) => !tenantFilter || r.tenantContactId === tenantFilter)
			.filter((r) => !periodFilter || r.period === periodFilter)
			.filter((r) => !paymentFilter || r.paymentStatus === paymentFilter)
			.filter((r) => !reasonFilter || r.incompleteReason === reasonFilter)
			.filter((r) => {
				if (!q) return true
				return r.propertyTitle?.toLowerCase().includes(q)
					|| r.propertyAddress?.toLowerCase().includes(q)
					|| r.tenantName?.toLowerCase().includes(q)
					|| r.id.toLowerCase().includes(q)
			})
	}, [rentals, search, propertyFilter, tenantFilter, periodFilter, paymentFilter, reasonFilter])

	const sorted = useMemo(() => {
		const arr = [...filtered]
		const dir = sortDir === 'asc' ? 1 : -1
		arr.sort((a, b) => {
			switch (sortField) {
				case 'property':
					return (a.propertyTitle ?? '').localeCompare(b.propertyTitle ?? '') * dir
				case 'tenant':
					return (a.tenantName ?? '').localeCompare(b.tenantName ?? '') * dir
				case 'price':
					return (Number(a.rentAmount) - Number(b.rentAmount)) * dir
				case 'nextPayment':
					return (new Date(a.nextPaymentDate ?? 0).getTime()
						- new Date(b.nextPaymentDate ?? 0).getTime()) * dir
				case 'startDate':
				case 'daysUntilStart':
					return (new Date(a.startDate).getTime() - new Date(b.startDate).getTime()) * dir
			}
		})
		return arr
	}, [filtered, sortField, sortDir])

	const paged = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

	function toggleSort(field: SortField) {
		if (sortField === field) {
			setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
		} else {
			setSortField(field)
			setSortDir('asc')
		}
	}

	const propertyOptions = useMemo<[string, string][]>(() => {
		return (propertiesData?.properties ?? []).map((p) => [p.id, formatPropertyAddress(p.city, p.address)])
	}, [propertiesData])

	const tenantOptions = useMemo<[string, string][]>(() => {
		return (contactsData?.items ?? []).map((c) => [c.id, c.name])
	}, [contactsData])

	// A deep-linked property preset is not a user-applied filter — count the
	// property filter as active only once the user changes it, so the
	// status-tailored empty state and its CTA stay visible on deep links.
	const isPropertyFilterActive = !!propertyFilter && propertyFilter !== initialPropertyId
	const hasActiveFilters = !!(search || isPropertyFilterActive || tenantFilter || periodFilter || paymentFilter || reasonFilter)

	if (isLoading && !data) {
		return (
			<div className="flex h-full items-center justify-center">
				<Spinner />
			</div>
		)
	}

	// Full-page error only when there is nothing to show — a failed background
	// refetch (every rental mutation invalidates the list) keeps the table.
	if (error && !data) {
		return (
			<EmptyState
				illustration={<IllustrationEmpty />}
				title={t('rent.loadErrorTitle')}
				description={t('rent.loadErrorDescription')}
				action={{ label: t('rent.loadErrorRetry'), onClick: () => { void refetch() } }}
			/>
		)
	}

	return (
		<div className="flex h-full flex-col">
			{withClosingTabs && (
				<div className="px-4 pt-3">
					<Tabs value={scope} onValueChange={(v) => { setScope(v as ClosingScope); setPage(1) }}>
						<TabsList>
							<TabsTrigger value="ALL">{t('rent.scopeTabs.all')}</TabsTrigger>
							<TabsTrigger value="ACTIVE">{t('rent.scopeTabs.active')}</TabsTrigger>
							<TabsTrigger value="CLOSING">{t('rent.scopeTabs.closing')}</TabsTrigger>
						</TabsList>
					</Tabs>
				</div>
			)}
			<PanelFilters
				filters={
					<>
						<Select
							value={propertyFilter || 'all'}
							onValueChange={(v) => { setPropertyFilter(v === 'all' ? '' : v); setPage(1) }}
						>
							<SelectTrigger width="lg" tone="muted">
								<SelectValue placeholder={t('rent.filters.property')} />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">{t('rent.filters.allProperties')}</SelectItem>
								{propertyOptions.map(([id, label]) => (
									<SelectItem key={id} value={id}>{label}</SelectItem>
								))}
							</SelectContent>
						</Select>
						<Select
							value={tenantFilter || 'all'}
							onValueChange={(v) => { setTenantFilter(v === 'all' ? '' : v); setPage(1) }}
						>
							<SelectTrigger width="lg" tone="muted">
								<SelectValue placeholder={t('rent.filters.tenant')} />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">{t('rent.filters.allTenants')}</SelectItem>
								{tenantOptions.map(([id, label]) => (
									<SelectItem key={id} value={id}>{label}</SelectItem>
								))}
							</SelectContent>
						</Select>
						<Select
							value={periodFilter || 'all'}
							onValueChange={(v) => { setPeriodFilter(v === 'all' ? '' : v as RentalPeriodicity); setPage(1) }}
						>
							<SelectTrigger width="md" tone="muted">
								<SelectValue placeholder={t('rent.filters.period')} />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">{t('rent.filters.allPeriods')}</SelectItem>
								{RENTAL_PERIODS.map((p) => (
									<SelectItem key={p} value={p}>{t(`rent.periods.${p.toLowerCase()}`)}</SelectItem>
								))}
							</SelectContent>
						</Select>
						{showReasonFilter && (
							<Select
								value={reasonFilter || 'all'}
								onValueChange={(v) => { setReasonFilter(v === 'all' ? '' : v as IncompleteReason); setPage(1) }}
							>
								<SelectTrigger width="md" tone="muted">
									<SelectValue placeholder={t('rent.filters.incompleteReason')} />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="all">{t('rent.filters.allReasons')}</SelectItem>
									{INCOMPLETE_REASONS.map((r) => (
										<SelectItem key={r} value={r}>{t(INCOMPLETE_REASON_KEYS[r])}</SelectItem>
									))}
								</SelectContent>
							</Select>
						)}
						{showPaymentFilter && (
							<Select
								value={paymentFilter || 'all'}
								onValueChange={(v) => { setPaymentFilter(v === 'all' ? '' : v as PaymentStatus); setPage(1) }}
							>
								<SelectTrigger width="md" tone="muted">
									<SelectValue placeholder={t('rent.filters.paymentStatus')} />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="all">{t('rent.filters.allStatuses')}</SelectItem>
									{PAYMENT_STATUSES.map((s) => (
										<SelectItem key={s} value={s}>{t(PAYMENT_STATUS_KEYS[s])}</SelectItem>
									))}
								</SelectContent>
							</Select>
						)}
					</>
				}
				actions={
					<>
						<SearchInput
							placeholder={t('rent.search')}
							value={search}
							className="w-[200px]"
							onChange={(e) => { setSearch(e.target.value); setPage(1) }}
						/>
						{showAddButton && (
							<Button size="sm" onClick={() => setAddOpen(true)}>
								{t('rent.addRental')}
							</Button>
						)}
					</>
				}
			/>

			{sorted.length === 0 ? (
				hasActiveFilters ? (
					<EmptyState
						illustration={<IllustrationEmpty />}
						title={t('rent.emptyFilterTitle')}
						description={t('rent.emptyFilterDescription')}
					/>
				) : (
					<EmptyState
						illustration={<IllustrationEmpty />}
						title={emptyTitle}
						description={emptyDescription}
						{...(showAddButton ? { action: { label: t('rent.addRental'), onClick: () => setAddOpen(true) } } : {})}
					/>
				)
			) : (
				<>
					<div className="flex min-h-0 flex-1 flex-col overflow-auto px-4">
						<div className="overflow-hidden rounded-lg border border-(--color-border)">
							<Table>
								<TableHeader>
									<TableRow className="hover:bg-transparent">
										<TableHead>
											<Button
												variant="ghost"
												iconRight={<ArrowUpDownIcon className="size-3.5" />}
												onClick={() => toggleSort('property')}
												className="h-auto px-1 py-0.5 gap-1 font-[500] text-(--color-muted-foreground) hover:bg-(--color-muted)/60"
											>
												{t('rent.table.property')}
											</Button>
										</TableHead>
										<TableHead className="w-[160px]">
											<Button
												variant="ghost"
												iconRight={<ArrowUpDownIcon className="size-3.5" />}
												onClick={() => toggleSort('tenant')}
												className="h-auto px-1 py-0.5 gap-1 font-[500] text-(--color-muted-foreground) hover:bg-(--color-muted)/60"
											>
												{t('rent.table.tenant')}
											</Button>
										</TableHead>
										<TableHead className="w-[160px]">
											<Button
												variant="ghost"
												iconRight={<ArrowUpDownIcon className="size-3.5" />}
												onClick={() => toggleSort('price')}
												className="h-auto px-1 py-0.5 gap-1 font-[500] text-(--color-muted-foreground) hover:bg-(--color-muted)/60"
											>
												{t('rent.table.price')}
											</Button>
										</TableHead>
										<TableHead className="w-[140px]">
											<Button
												variant="ghost"
												iconRight={<ArrowUpDownIcon className="size-3.5" />}
												onClick={() => toggleSort('startDate')}
												className="h-auto px-1 py-0.5 gap-1 font-[500] text-(--color-muted-foreground) hover:bg-(--color-muted)/60"
											>
												{t('rent.table.period')}
											</Button>
										</TableHead>
										{isClosing && (
											<>
												<TableHead className="w-[200px] font-[500] text-(--color-muted-foreground)">
													{t('rent.table.incompleteReason')}
												</TableHead>
												<TableHead className="w-[56px]" />
											</>
										)}
										{isUpcoming && (
											<>
												<TableHead className="w-[140px]">
													<Button
														variant="ghost"
														iconRight={<ArrowUpDownIcon className="size-3.5" />}
														onClick={() => toggleSort('startDate')}
														className="h-auto px-1 py-0.5 gap-1 font-[500] text-(--color-muted-foreground) hover:bg-(--color-muted)/60"
													>
														{t('rent.table.startDate')}
													</Button>
												</TableHead>
												<TableHead className="w-[140px]">
													<Button
														variant="ghost"
														iconRight={<ArrowUpDownIcon className="size-3.5" />}
														onClick={() => toggleSort('daysUntilStart')}
														className="h-auto px-1 py-0.5 gap-1 font-[500] text-(--color-muted-foreground) hover:bg-(--color-muted)/60"
													>
														{t('rent.table.daysUntilStart')}
													</Button>
												</TableHead>
											</>
										)}
										{isCompleted && (
											<TableHead className="w-[56px]" />
										)}
										{!isClosing && !isUpcoming && !isCompleted && (
											<>
												<TableHead className="w-[140px]">
													<Button
														variant="ghost"
														iconRight={<ArrowUpDownIcon className="size-3.5" />}
														onClick={() => toggleSort('nextPayment')}
														className="h-auto px-1 py-0.5 gap-1 font-[500] text-(--color-muted-foreground) hover:bg-(--color-muted)/60"
													>
														{t('rent.table.nextPayment')}
													</Button>
												</TableHead>
												<TableHead className="w-[140px] font-[500] text-(--color-muted-foreground)">
													{t('rent.table.paymentStatus')}
												</TableHead>
												<TableHead className="w-[56px]" />
											</>
										)}
										{isUpcoming && (
											<TableHead className="w-[56px]" />
										)}
									</TableRow>
								</TableHeader>
								<TableBody>
									{paged.map((r) => (
										<TableRow
											key={r.id}
											className="h-20 cursor-pointer"
											onClick={() => navigate({ to: '/rent/$rentalId', params: { rentalId: r.id } })}
										>
											<TableCell className="px-4">
												<div className="flex items-center gap-3">
													{r.propertyImage ? (
														<img
															src={r.propertyImage}
															alt={r.propertyTitle ?? ''}
															className="size-12 shrink-0 rounded-xl object-cover"
														/>
													) : (
														<div className="size-12 shrink-0 rounded-xl bg-(--color-muted)" />
													)}
													<div className="flex min-w-0 flex-col gap-1">
														<span className="flex items-center gap-2 truncate text-sm font-[500] leading-none text-(--color-foreground)">
															{r.propertyTitle ?? r.propertyAddress}
															{showScopeBadges && r.status === 'CLOSING' && (
																<Badge variant="warning" size="sm">
																	{t('rent.scopeTabs.closing')}
																</Badge>
															)}
														</span>
														{/* propertyTitle already includes the address — skip the subtitle then. */}
														{r.propertyAddress && r.propertyTitle && !r.propertyTitle.includes(r.propertyAddress) && (
															<span className="truncate text-sm leading-5 text-(--color-muted-foreground)">
																{r.propertyAddress}
															</span>
														)}
													</div>
												</div>
											</TableCell>
											<TableCell className="px-4 text-sm" onClick={(e) => e.stopPropagation()}>
												{r.tenantContactId ? (
													<button
														type="button"
														className="text-(--color-info) underline-offset-2 hover:underline"
														onClick={() => navigate({ to: '/tenants/$tenantId', params: { tenantId: r.tenantContactId } })}
													>
														{r.tenantName ?? `Арендатор №${r.tenantContactId.slice(-6)}`}
													</button>
												) : (
													<span className="text-(--color-muted-foreground)">—</span>
												)}
											</TableCell>
											<TableCell className="px-4 text-sm text-(--color-foreground)">
												{formatPrice(r.rentAmount, r.currency, r.period, t)}
											</TableCell>
											<TableCell className="px-4 text-sm text-(--color-foreground) whitespace-nowrap">
												{isCompleted
													? `${formatDate(r.startDate)} - ${r.endDate ? formatDate(r.endDate) : '—'}`
													: <>{formatDate(r.startDate)} -<br />{r.endDate ? formatDate(r.endDate) : '—'}</>
												}
											</TableCell>
											{isClosing && (
												<>
													<TableCell className="w-[200px] px-4">
														{r.incompleteReason ? (
															<Badge variant={INCOMPLETE_REASON_VARIANTS[r.incompleteReason]} size="sm">
																{t(INCOMPLETE_REASON_KEYS[r.incompleteReason])}
															</Badge>
														) : (
															<span className="text-(--color-muted-foreground)">—</span>
														)}
													</TableCell>
													<TableCell className="w-[56px] px-4">
														<IconButton variant="ghost" size="sm" aria-label={t('rent.actions.viewSchedule')}>
															<CalendarIcon className="size-4" />
														</IconButton>
													</TableCell>
												</>
											)}
											{isUpcoming && (() => {
												const days = daysUntil(r.startDate)
												return (
													<>
														<TableCell className="px-4 text-sm text-(--color-foreground)">
															{formatDate(r.startDate)}
														</TableCell>
														<TableCell className="px-4">
															<Badge
																variant={daysUntilVariant(days)}
																size="sm"
																className={days <= 7 ? 'bg-red-100 text-red-700 hover:bg-red-200' : undefined}
															>
																{durationLabel(days, t)}
															</Badge>
														</TableCell>
													</>
												)
											})()}
											{isCompleted && (
												<TableCell className="w-[56px] px-4">
													<IconButton variant="ghost" size="sm" aria-label={t('rent.actions.viewSchedule')}>
														<CalendarIcon className="size-4" />
													</IconButton>
												</TableCell>
											)}
											{!isClosing && !isUpcoming && !isCompleted && (
												<>
													<TableCell className="px-4 text-sm text-(--color-foreground)">
														{r.nextPaymentDate ? formatDate(r.nextPaymentDate) : '—'}
													</TableCell>
													<TableCell className="px-4">
														{r.paymentStatus && (
															<Badge variant={PAYMENT_STATUS_VARIANTS[r.paymentStatus]} size="sm">
																{t(PAYMENT_STATUS_KEYS[r.paymentStatus])}
															</Badge>
														)}
													</TableCell>
													<TableCell className="w-[56px] px-4" onClick={(e) => e.stopPropagation()}>
														<DropdownMenu>
															<DropdownMenuTrigger render={<IconButton variant="ghost" size="sm" aria-label={t('rent.actions.more')} />}>
																<EllipsisVerticalIcon className="size-4" />
															</DropdownMenuTrigger>
															<DropdownMenuContent align="end" className="min-w-[200px]">
																<DropdownMenuItem onClick={() => { setSelectedRental(r); setExtendOpen(true) }}>
																	{t('rent.actions.extend', { defaultValue: 'Продление аренды' })}
																</DropdownMenuItem>
															</DropdownMenuContent>
														</DropdownMenu>
													</TableCell>
												</>
											)}
											{isUpcoming && (
												<TableCell className="w-[56px] px-4" onClick={(e) => e.stopPropagation()}>
													<DropdownMenu>
														<DropdownMenuTrigger render={<IconButton variant="ghost" size="sm" aria-label={t('rent.actions.more')} />}>
															<EllipsisVerticalIcon className="size-4" />
														</DropdownMenuTrigger>
														<DropdownMenuContent align="end" className="min-w-44">
															<DropdownMenuItem onClick={() => navigate({ to: '/rent/$rentalId', params: { rentalId: r.id } })}>
																{t('rent.actions.open')}
															</DropdownMenuItem>
														</DropdownMenuContent>
													</DropdownMenu>
												</TableCell>
											)}
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
					</div>
					<PanelDown total={sorted.length} page={page} onPageChange={setPage} limit={PAGE_SIZE} />
				</>
			)}

			<StartRentalDialog open={addOpen} onOpenChange={setAddOpen} />

			{selectedRental && (
				<>
					<EditRentalDialog
						rental={selectedRental}
						open={extendOpen}
						onOpenChange={setExtendOpen}
					/>
				</>
			)}
		</div>
	)
}
